import { useEffect, useRef, useState } from 'react'
import { Copy, Eye, Loader2, Plus, Radio, Trash2, Video } from 'lucide-react'
import { Link } from 'react-router-dom'
import { DrawerSection } from '@/shared/ui/Drawer'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { Modal } from '@/shared/ui/Modal'
import { useCan } from '@/shared/auth/session'
import { fmtNumber } from '@/shared/lib/format'
import { createCoverageFeed, deleteCoverageFeed } from '../api'
import { LiveCoveragePlayer, fmtClock } from '../players'
import { feedKindIcon, feedStatusLabel, recordingStateLabel } from '../vocab'
import { explain } from '@/shared/api/problem'
import type { CoverageEventResponse, CoverageFeedResponse } from '@/shared/api/types'

const MAX_FEEDS = 8

export function FeedsPanel({
  event, onChanged,
}: { event: CoverageEventResponse; onChanged: () => void }) {
  const canUpdate = useCan('coverage:update')
  const canDelete = useCan('coverage:delete')
  const canWatch = useCan('coverage:watch')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [watching, setWatching] = useState<CoverageFeedResponse | null>(null)

  const over = event.statusCode === 'ENDED' || event.statusCode === 'CANCELLED'

  return (
    <div className="space-y-3">
      <ErrorStrip message={error} />

      <DrawerSection
        title="Cameras"
        right={canUpdate && event.statusCode !== 'CANCELLED' && event.feeds.length < MAX_FEEDS ? (
          <button
            type="button"
            className="erp-btn h-7"
            disabled={busy}
            onClick={async () => {
              setBusy(true); setError(null)
              try {
                await createCoverageFeed(event.id, { title: `Feed ${event.feeds.length + 1}` })
                onChanged()
              } catch (e) { setError(explain(e)) } finally { setBusy(false) }
            }}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Add camera
          </button>
        ) : undefined}
      >
        {over && (
          <div className="mb-2">
            <Note tone="info">
              This event is marked {event.statusCode === 'CANCELLED' ? 'cancelled' : 'over'}.
              You can still add a camera and try to broadcast — ending an event covers a lot
              of different situations and most of them are not "never film this again". If
              the server refuses, it will say why, and <strong>Reopen</strong> in the header
              puts the event back to scheduled without touching anything recorded.
            </Note>
          </div>
        )}

        {event.feeds.length === 0 ? (
          <Note tone="info">
            No cameras yet. Add one, then send its broadcast link to whoever is holding the
            phone — or open Go Live and film from this device.
          </Note>
        ) : (
          <ul className="space-y-1.5">
            {event.feeds.map((f) => {
              const live = f.statusCode === 'LIVE'
              return (
                <li key={f.id} className="rounded-md border border-border p-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={live ? 'text-red-600' : 'text-muted-foreground'}>
                      {feedKindIcon(f.kindCode)}
                    </span>
                    <span className="truncate text-erp-sm font-medium">{f.title}</span>
                    <span className={live
                      ? 'rounded bg-red-600 px-1.5 py-0.5 text-erp-xs font-semibold text-white'
                      : 'text-erp-xs text-muted-foreground'}>
                      {live ? 'LIVE' : feedStatusLabel(f.statusCode)}
                    </span>
                    {live && (
                      <span className="flex items-center gap-1 text-erp-xs text-muted-foreground">
                        <Eye className="h-3 w-3" /> {fmtNumber(f.liveViewers)}
                      </span>
                    )}
                    <span className="ml-auto flex shrink-0 items-center gap-1">
                      {live && canWatch && (
                        <button type="button" className="erp-btn h-7"
                          onClick={() => setWatching(f)}>
                          <Video className="h-3.5 w-3.5" /> Watch
                        </button>
                      )}
                      {/*
                        A camera that ENDED can go on air again, and this is the
                        button for it. A broadcast stopped by a dropped connection,
                        a flat battery or a thumb on the wrong control is not a
                        decision to stop covering the event — and the archive
                        handles it correctly, because each session is its own
                        recording rather than more bytes appended to the last one.
                      */}
                      {/*
                        OFFERED FOR EVERY CAMERA THAT IS NOT CURRENTLY LIVE,
                        including one that ENDED and one on an event somebody
                        closed.
                        A hidden button cannot be told apart from a broken one:
                        the previous version hid it whenever the event was over,
                        so somebody who ended an event by accident saw no control
                        and no reason. If a rule forbids it, the server says which
                        rule — and that sentence is worth more than the absence.
                      */}
                      {!live && event.statusCode !== 'CANCELLED' && (
                        <Link className="erp-btn h-7"
                          to={`/coverage/go-live?feed=${f.id}&event=${event.id}`}>
                          <Radio className="h-3.5 w-3.5" />
                          {f.statusCode === 'ENDED' ? 'Broadcast again' : 'Broadcast'}
                        </Link>
                      )}
                      <BroadcastLink feedId={f.id} eventId={event.id} />
                      {canDelete && !f.startedAt && (
                        <button
                          type="button"
                          className="erp-btn h-7"
                          aria-label={`Delete ${f.title}`}
                          onClick={async () => {
                            setError(null)
                            try { await deleteCoverageFeed(f.id); onChanged() }
                            catch (e) { setError(explain(e)) }
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </span>
                  </div>
                  <div className="mt-0.5 text-erp-xs text-muted-foreground">
                    {f.broadcasterName ?? 'Unclaimed'}
                    {f.durationSec != null && ` · last session ${fmtClock(f.durationSec)}`}
                    {f.peakViewers > 0 && ` · peak ${fmtNumber(f.peakViewers)}`}
                    {f.recordingStateCode !== 'NONE' && ` · ${recordingStateLabel(f.recordingStateCode)}`}
                  </div>
                </li>
              )
            })}
          </ul>
        )}

        {event.feeds.length >= MAX_FEEDS && (
          <div className="mt-2">
            <Note tone="info">
              Eight cameras is the limit for one event. Each is a separate publisher in the
              same room, and past eight the composite recording stops being watchable.
            </Note>
          </div>
        )}
      </DrawerSection>

      {watching && (
        <Modal open onClose={() => setWatching(null)} title={watching.title} width="lg">
          <LiveCoveragePlayer feedId={watching.id} className="aspect-video w-full" />
        </Modal>
      )}
    </div>
  )
}

/**
 * The link that goes to the phone.
 *
 * Absolute, because a relative URL is useless the moment it leaves this browser —
 * which is the entire purpose of the button.
 */
function BroadcastLink({ feedId, eventId }: { feedId: string; eventId: string }) {
  const [copied, setCopied] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])
  /**
   * BOTH ids, and the event id is the load-bearing one.
   *
   * `GET /coverage/events` returns no `feeds` — the service passes `List.of()` to
   * avoid an N+1 across 200 rows — so a page handed only a feed id has nothing to
   * resolve it against and has to scan every event's (empty) feed array. Carrying
   * the event id turns that into one detail fetch, which is both correct today and
   * cheaper than the scan would have been if it worked.
   */
  const url = `${window.location.origin}/broadcast?feed=${feedId}&event=${eventId}`

  return (
    <button
      type="button"
      className="erp-btn h-7"
      title={url}
      // The label is hidden below `sm`, and this feature is aimed at phones, so
      // without an explicit name it is an icon whose only description is a raw
      // URL in a title attribute.
      aria-label="Copy the broadcast link for this camera"
      onClick={async () => {
        try { await navigator.clipboard.writeText(url) }
        catch { window.prompt('Copy this link to the phone:', url) }
        setCopied(true)
        if (timer.current) clearTimeout(timer.current)
        timer.current = setTimeout(() => setCopied(false), 1800)
      }}
    >
      <Copy className="h-3.5 w-3.5" />
      <span className="hidden sm:inline">{copied ? 'Copied' : 'Link'}</span>
      {/* Announced; the colour change alone is not. */}
      <span className="sr-only" role="status">{copied ? 'Link copied' : ''}</span>
    </button>
  )
}
